import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Box,
  RefreshCw,
  Award,
  CheckCircle2,
  Package,
  Truck,
  RotateCcw,
  AlertCircle,
  ChevronDown,
  Mail,
  Send,
  Lock,
  Phone,
  Globe
} from 'lucide-react';

const ReturnsAndRefunds = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const keyGuarantees = [
    {
      title: '60 Day Returns',
      sub: 'Return within 60 days of delivery',
      icon: Calendar
    },
    {
      title: 'Hassle Free',
      sub: 'Easy return process with our support team',
      icon: Box
    },
    {
      title: 'Quick Refunds',
      sub: 'Refunds processed within 5-7 business days',
      icon: RefreshCw
    },
    {
      title: 'Quality Guaranteed',
      sub: '2 Year Warranty on all our devices',
      icon: Award
    }
  ];

  const returnSteps = [
    {
      num: '1',
      title: 'Request a Return',
      desc: 'Contact our support team or start a return from your order page.',
      icon: Package
    },
    {
      num: '2',
      title: 'Pack Your Item',
      desc: 'Securely pack the item in original packaging with all included accessories.',
      icon: Box
    },
    {
      num: '3',
      title: 'Ship It Back',
      desc: 'Use the prepaid return label we provide and drop it off at the nearest carrier.',
      icon: Truck
    },
    {
      num: '4',
      title: 'Get Your Refund',
      desc: 'Once we receive and inspect the item, we’ll process your refund within 5-7 business days.',
      icon: RotateCcw
    }
  ];

  const refundTable = [
    { method: 'Credit / Debit Card', timeline: '5-7 Business Days' },
    { method: 'PayPal', timeline: '5-7 Business Days' },
    { method: 'Shop Pay / Apple Pay / Google Pay', timeline: '5-7 Business Days' },
    { method: 'Bank Transfer / UPI', timeline: '5-10 Business Days' }
  ];

  const faqs = [
    {
      q: 'How long do I have to return an item?',
      a: 'You have 60 full days from the date of delivery to initiate a return or exchange for any InfraEdge product.'
    },
    {
      q: 'Can I exchange my item?',
      a: 'Yes! If you prefer a different panel size or model, we can arrange an exchange. Contact our team to get started.'
    },
    {
      q: 'Who pays for return shipping?',
      a: 'For eligible returns, we provide a prepaid return shipping label. If the return is due to an error on our part (damaged/defective), shipping is 100% free.'
    },
    {
      q: 'What if I received a damaged or defective item?',
      a: 'All InfraEdge panels are backed by a 2-Year Warranty. Contact support immediately with photos/videos for a free replacement.'
    },
    {
      q: 'When will I receive my refund?',
      a: 'Once your return is received and inspected at our warehouse, refunds are issued back to your original payment method within 5-7 business days.'
    },
    {
      q: 'How do I track my return?',
      a: 'You will receive an email confirmation containing the tracking number for your prepaid return shipment.'
    }
  ];

  const toggleFaq = (idx) => {
    setOpenFaqIndex(openFaqIndex === idx ? null : idx);
  };

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>Returns & Refunds</span>
        </div>

        {/* TOP HERO HEADER WITH RED RETURN PACKAGE EMBLEM */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', alignItems: 'center', marginBottom: '40px' }}>
          <div>
            <h1 style={{ fontSize: '44px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Returns & Refunds
            </h1>
            <div style={{ width: '40px', height: '3px', background: '#dc2626', borderRadius: '2px', marginBottom: '20px' }} />
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, margin: 0 }}>
              We want you to love your InfraEdge experience. If something isn’t right, we’re here to make it right with our simple and transparent return policy.
            </p>
          </div>

          {/* Right Red Return 3D Package Emblem */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '260px', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'absolute', width: '240px', height: '240px', border: '2px dashed #fecaca', borderRadius: '50%', pointerEvents: 'none' }} />
              
              {/* Red Package Box */}
              <div style={{ width: '130px', height: '130px', background: '#ffffff', border: '2px solid #dc2626', borderRadius: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 30px rgba(220,38,38,0.15)', zIndex: 1, position: 'relative' }}>
                <RotateCcw size={48} color="#dc2626" />
                <div style={{ position: 'absolute', bottom: '-10px', right: '-10px', width: '38px', height: '38px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #ffffff', boxShadow: '0 4px 12px rgba(220,38,38,0.3)' }}>
                  <CheckCircle2 size={22} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4 KEY GUARANTEES ROW */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '48px' }}>
          {keyGuarantees.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 16px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 900, color: '#0f172a', marginBottom: '2px' }}>{item.title}</div>
                  <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>{item.sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 1. RETURN POLICY DETAILS */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '36px 32px', marginBottom: '40px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '16px' }}>
            Return Policy
          </h2>
          <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.7, marginBottom: '24px' }}>
            You may return most new, unopened items within 60 days of delivery for a full refund or exchange. Items must be in original condition and packaging.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { label: '60 Day Return Window', text: 'You have 60 days from the date of delivery to initiate a return.' },
              { label: 'Condition', text: 'Items must be unused, in original condition, and include all original packaging, accessories, manuals, and free gifts.' },
              { label: 'Proof of Purchase', text: 'A valid order number or receipt is required to process your return.' },
              { label: 'Non-Returnable Items', text: 'Opened hygiene products, gift cards, and final sale items cannot be returned.' },
              { label: 'Return Shipping', text: 'For eligible returns, we provide a prepaid return label. Returns sent without authorization may not be accepted.' }
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <CheckCircle2 size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.6 }}>
                  <strong style={{ color: '#0f172a', fontWeight: 800 }}>{item.label}:</strong> {item.text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. HOW TO RETURN AN ITEM (4 STEP CARDS ROW) */}
        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '24px' }}>
            How to Return an Item
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {returnSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.num} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px 20px', textAlign: 'center', position: 'relative', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                  
                  {/* Step Badge */}
                  <div style={{ position: 'absolute', top: '16px', left: '16px', width: '28px', height: '28px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: '12.5px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {step.num}
                  </div>

                  <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '12px auto 16px' }}>
                    <Icon size={22} />
                  </div>

                  <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 3. REFUND INFORMATION TABLE & PLEASE NOTE BOX */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '36px 32px', marginBottom: '40px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '12px' }}>
            Refund Information
          </h2>
          <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '24px' }}>
            Refunds will be issued to your original payment method after we receive and inspect your return. Please allow 5-7 business days for the refund to reflect in your account.
          </p>

          {/* Table */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#f8fafc', padding: '14px 20px', fontWeight: 800, fontSize: '12.5px', color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>
              <div>Payment Method</div>
              <div>Refund Timeline</div>
            </div>
            {refundTable.map((row, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '14px 20px', fontSize: '13px', color: '#475569', borderBottom: idx === refundTable.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.method}</div>
                <div>{row.timeline}</div>
              </div>
            ))}
          </div>

          {/* Please Note Box */}
          <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '16px 20px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ fontSize: '13px', color: '#dc2626', fontWeight: 900, display: 'block', marginBottom: '2px' }}>Please Note</strong>
              <span style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5 }}>
                Shipping costs are non-refundable unless the return is due to our error (damaged, defective, or incorrect item).
              </span>
            </div>
          </div>
        </div>

        {/* 5. BOTTOM STAY UPDATED NEWSLETTER BANNER */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '20px', padding: '28px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Mail size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>Stay Updated</h4>
              <p style={{ fontSize: '12.5px', color: '#475569', margin: 0 }}>
                Subscribe to get special offers, wellness tips, and updates on new products.
              </p>
            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Enter your email"
              style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '9999px', padding: '12px 20px', fontSize: '13px', outline: 'none', width: '220px' }}
            />
            <button type="submit" style={{ background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '9999px', padding: '12px 24px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.25)' }}>
              Subscribe
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default ReturnsAndRefunds;
